import { useMemo } from "react";
import { StyledLineChart, StyledAreaChart, StyledBarChart } from "../ChartComponents";
import StatCard from "./StatCard";

function formatAvgQueriesPerChat(value) {
    return Number.isFinite(value) ? value.toFixed(2) : "0.00";
}

function ChartSlot({ fallbackTitle, chart, ChartComponent }) {
    const title = chart?.title || fallbackTitle;

    if (!chart) {
        return (
            <div className="min-w-0 w-full rounded-xl border bg-white p-6 shadow-sm">
                <h3 className="text-sm font-semibold text-slate-900 mb-4">{title}</h3>
                <div className="flex h-[200px] items-center justify-center text-sm text-slate-400">
                    Not available
                </div>
            </div>
        );
    }

    const chartData = (chart.points || []).map((p) => ({ date: p.period, count: p.count }));
    const isEmpty = chartData.every((item) => (item.count || 0) === 0);

    return (
        <div className="min-w-0 w-full rounded-xl border bg-white p-6 shadow-sm">
            <h3 className="text-sm font-semibold text-slate-900 mb-4">{title}</h3>
            {isEmpty ? (
                <div className="flex h-[200px] items-center justify-center text-sm text-slate-400">
                    No activity in this time range
                </div>
            ) : (
                <ChartComponent
                    data={chartData}
                    xKey="date"
                    yKey="count"
                    yAxisLabel={chart.y_axis_label}
                />
            )}
        </div>
    );
}

export default function AgentAnalytics({ agentAnalytics, loading }) {
    const agents = useMemo(() => agentAnalytics?.agents || [], [agentAnalytics]);

    if (loading) {
        return (
            <div className="rounded-xl border bg-white p-6 shadow-sm">
                <div className="flex h-[300px] items-center justify-center text-sm text-slate-400">
                    Loading agent analytics…
                </div>
            </div>
        );
    }

    if (agents.length === 0) {
        return (
            <div className="rounded-xl border bg-white p-6 shadow-sm">
                <div className="flex h-[200px] items-center justify-center text-sm text-slate-400">
                    No agent analytics available for this facility
                </div>
            </div>
        );
    }

    return (
        <div className="w-full space-y-6">
            {agents.map((agent) => (
                <div key={agent.agent_id} className="min-w-0 w-full rounded-xl border bg-white p-6 shadow-sm">
                    <h2 className="text-lg font-semibold text-slate-900 mb-4">{agent.agent_name}</h2>

                    <div className="grid grid-cols-2 gap-4 lg:grid-cols-4 mb-6">
                        <StatCard label="Total Queries" value={agent.kpis?.total_queries ?? 0} />
                        <StatCard label="Total Chats" value={agent.kpis?.total_chats ?? 0} />
                        <StatCard
                            label="Avg Queries / Chat"
                            value={formatAvgQueriesPerChat(agent.kpis?.avg_queries_per_chat)}
                        />
                        <StatCard label="Active Users" value={agent.kpis?.active_users ?? 0} />
                    </div>

                    <div className="flex flex-col gap-6">
                        <ChartSlot
                            fallbackTitle="Query Volume"
                            chart={agent.charts?.query_volume}
                            ChartComponent={StyledLineChart}
                        />
                        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                            <ChartSlot
                                fallbackTitle="Cumulative Adoption Curve"
                                chart={agent.charts?.cumulative_query_volume}
                                ChartComponent={StyledAreaChart}
                            />
                            <ChartSlot
                                fallbackTitle="Queries by Week"
                                chart={agent.charts?.weekly_query_volume}
                                ChartComponent={StyledBarChart}
                            />
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}
